import { DiscountTypeEnum } from "@/features/shared";

/**
 * Every money figure on a purchase return is derived from the returned goods,
 * the returned expenses and the tax/discount switches. Deriving it in one pure
 * place keeps the form from storing values it would have to keep in sync, and
 * lets the price summary and the submit payload agree by construction.
 *
 * Note the split between `goodsGrandTotal` and `grandTotal`: the backend
 * validates the submitted grand total against the goods figure alone and adds
 * the returned expenses itself, so the payload must send the former while the
 * user is shown the latter. (This is the mirror image of a sales return, where
 * the client sends the with-expenses total and the backend subtracts.)
 */

export const computeSubtotal = (returnableProductVariants = []) =>
  returnableProductVariants.reduce(
    (total, variant) =>
      total +
      (variant.price || 0) * (variant.quantity || 0) -
      (variant.discount || 0),
    0
  );

export const computeTotalExpense = (purchaseReturnExpenses = []) =>
  purchaseReturnExpenses.reduce(
    (total, expense) => total + (expense.amount || 0),
    0
  );

export const computeDiscount = ({ subtotal, discountType, discountAmount }) =>
  discountType === DiscountTypeEnum.PERCENTAGE && discountAmount
    ? (subtotal * discountAmount) / 100
    : discountAmount || 0;

export const computePurchaseReturnTotals = ({
  returnableProductVariants,
  purchaseReturnExpenses,
  discountType,
  discountAmount,
  taxable,
  totalIncludesTax,
  taxPercentage,
}) => {
  const subtotal = computeSubtotal(returnableProductVariants);
  const totalExpense = computeTotalExpense(purchaseReturnExpenses);
  const discount = computeDiscount({ subtotal, discountType, discountAmount });

  let taxAmount = 0;
  let goodsGrandTotal = subtotal - discount;

  if (taxable) {
    if (totalIncludesTax) {
      taxAmount = Math.round(
        (goodsGrandTotal * taxPercentage) / (100 + taxPercentage)
      );
    } else {
      taxAmount = Math.round((goodsGrandTotal * taxPercentage) / 100);
      goodsGrandTotal += taxAmount;
    }
  }

  return {
    subtotal,
    totalExpense,
    discount,
    taxAmount,
    // What the payload sends — the backend adds the expenses on its side.
    goodsGrandTotal,
    // What the document is actually worth, and what the summary shows.
    grandTotal: goodsGrandTotal + totalExpense,
  };
};
